import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Reorder } from "framer-motion";
import { toast } from "sonner";
import { api, photoSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TopBar, BottomNav } from "../components/Shell";
import PhotoEditor from "../components/PhotoEditor";
import ProfileCard from "../components/ProfileCard";
import { X, Star, LogOut, Loader2, Camera, Link2, Check, ChevronDown, Eye, GripVertical, Bell, BellOff, Sparkles, ImagePlus, Lock } from "lucide-react";
import { AREAS } from "../lib/areas";
import { enablePush, disablePush, pushSupported, pushEnabled, isIOS, isStandalone } from "../lib/push";

const INTERESTS = ["Coffee", "Foodie", "Music", "Fitness", "Travel", "Movies", "Fort Explorer", "Reading", "Gaming", "Photography", "Cricket", "Dancing"];
const GENDERS = [{ v: "female", l: "Woman" }, { v: "male", l: "Man" }, { v: "other", l: "Other" }];

function prescaleImage(file, maxDim = 1600, quality = 0.9) {
  // Android Chrome struggles with full-size camera photos in the cropper's canvas
  // (memory limits -> blank editor). Downscale first; small files pass through.
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      if (scale >= 1) { resolve({ url, file }); return; }
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => {
        if (!b) { URL.revokeObjectURL(url); reject(new Error("prescale failed")); return; }
        URL.revokeObjectURL(url);
        const scaled = new File([b], file.name, { type: "image/jpeg" });
        resolve({ url: URL.createObjectURL(scaled), file: scaled });
      }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });
}

export default function Profile() {
  const { user, setUser, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const pendingFile = useRef(null);
  const [form, setForm] = useState({
    first_name: user.first_name || "", last_name: user.last_name || "",
    dob: user.dob || "", gender: user.gender || "",
    neighborhood: user.neighborhood || "", bio: user.bio || "",
    interests: user.interests || [],
  });
  const identityLocked = !!user.identity_locked || user.aadhaar_status === "approved";
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [showUrl, setShowUrl] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [editSrc, setEditSrc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [order, setOrder] = useState(user.photos || []);
  const [pushState, setPushState] = useState("idle"); // idle|on|off|unsupported|denied|working
  const [bioLoading, setBioLoading] = useState(false);
  const [showAvatar, setShowAvatar] = useState(false);
  const [avatarPrompt, setAvatarPrompt] = useState("");
  const [avatarLoading, setAvatarLoading] = useState(false);

  const photos = user.photos || [];

  useEffect(() => { setOrder(user.photos || []); }, [user.photos]);

  useEffect(() => {
    (async () => {
      if (!pushSupported()) { setPushState("unsupported"); return; }
      if (Notification.permission === "denied") { setPushState("denied"); return; }
      setPushState((await pushEnabled()) ? "on" : "off");
    })();
  }, []);

  const togglePush = async () => {
    if (pushState === "on") {
      await disablePush();
      setPushState("off");
      toast("Notifications turned off");
      return;
    }
    setPushState("working");
    try {
      await enablePush();
      setPushState("on");
      toast.success("Notifications enabled");
    } catch (e) {
      setPushState(e.message === "denied" ? "denied" : "off");
      toast.error("Notifications not enabled");
    }
  };

  const toggleInterest = (t) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(t) ? f.interests.filter((x) => x !== t) : [...f.interests, t] }));

  const genBio = async () => {
    setBioLoading(true);
    try {
      const { data } = await api.post("/ai/bio", { keywords: form.bio.trim() });
      setForm((f) => ({ ...f, bio: data.bio }));
      toast.success("AI bio ready — tweak it or save");
    } catch { toast.error("AI couldn't write a bio right now"); }
    finally { setBioLoading(false); }
  };

  const genAvatar = async () => {
    if (!avatarPrompt.trim()) { toast.error("Describe your avatar first"); return; }
    setAvatarLoading(true);
    try {
      await api.post("/ai/avatar", { prompt: avatarPrompt.trim() });
      await refreshUser();
      toast.success("AI avatar added — remove it if you don't like it");
      setShowAvatar(false); setAvatarPrompt("");
    } catch { toast.error("AI couldn't generate an avatar right now"); }
    finally { setAvatarLoading(false); }
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.dob || !form.gender) {
      toast.error("First name, last name, date of birth and gender are required");
      return;
    }
    setSaving(true);
    try {
      const payload = identityLocked
        ? { neighborhood: form.neighborhood, bio: form.bio, interests: form.interests }
        : {
            first_name: form.first_name.trim(), last_name: form.last_name.trim(),
            dob: form.dob, gender: form.gender,
            neighborhood: form.neighborhood, bio: form.bio, interests: form.interests,
          };
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success("Profile saved");
      if (data.profile_complete) {
        navigate(["none", "rejected"].includes(data.aadhaar_status || "none") ? "/verify-aadhaar" : "/");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    pendingFile.current = file;
    prescaleImage(file)
      .then(({ url, file: f }) => { pendingFile.current = f; setEditSrc(url); })
      .catch(() => uploadBlob(file)); // never dead-end: upload original directly if editor prep fails
  };

  const uploadBlob = async (blob) => {
    if (!blob) blob = pendingFile.current; // editor/crop failure fallback
    if (!blob) { toast.error("Upload failed"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      const file = blob instanceof File ? blob : new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
      fd.append("file", file);
      await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Photo added");
      setEditSrc(null);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addUrl = async () => {
    if (!urlInput.trim()) return;
    try {
      await api.post("/profile/photo-url", { url: urlInput.trim() });
      await refreshUser();
      setUrlInput(""); setShowUrl(false);
      toast.success("Photo added");
    } catch { toast.error("Could not add photo"); }
  };

  const removePhoto = async (id) => {
    try { await api.delete(`/profile/photo/${id}`); await refreshUser(); } catch {}
  };

  const persistOrder = async (list) => {
    setOrder(list);
    try {
      const { data } = await api.post("/profile/photos/reorder", { photo_ids: list.map((p) => p.id) });
      setUser({ ...user, photos: data.photos });
    } catch { toast.error("Could not reorder"); }
  };

  return (
    <>
      <TopBar right={
        <button data-testid="logout-button" onClick={logout} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center text-slate-300 active:scale-90 transition-transform">
          <LogOut className="w-5 h-5" />
        </button>
      } />

      <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
        {!user.profile_complete && (
          <div className="mb-5 p-4 rounded-2xl bg-rose-500/10 border border-rose-400/30">
            <p className="text-sm text-rose-200 font-medium">Complete your profile to start swiping — add at least one photo, your age and gender.</p>
          </div>
        )}

        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Photos</label>
          {photos.length > 0 && (
            <button data-testid="preview-profile-button" onClick={() => setShowPreview(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-rose-400 px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-400/20">
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mb-2">Drag to reorder · the first photo is your main</p>
        <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 mb-3" data-testid="photo-grid">
          <Reorder.Group axis="x" values={order} onReorder={setOrder} className="flex gap-2.5">
            {order.map((p) => (
              <Reorder.Item key={p.id} value={p} onDragEnd={() => persistOrder(order)} data-testid="photo-item"
                className="relative w-24 aspect-[3/4] rounded-xl overflow-hidden bg-[#1D1829] border border-white/10 shrink-0 cursor-grab active:cursor-grabbing touch-none">
                <img src={photoSrc(p.url)} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
                {order[0]?.id === p.id && <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-amber-400 text-[9px] font-bold text-black flex items-center gap-0.5"><Star className="w-2.5 h-2.5" fill="currentColor" />Main</span>}
                <button onClick={() => removePhoto(p.id)} data-testid="photo-remove" className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 grid place-items-center text-white"><X className="w-3.5 h-3.5" /></button>
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-white/60"><GripVertical className="w-4 h-4" /></span>
              </Reorder.Item>
            ))}
          </Reorder.Group>
          {photos.length < 6 && (
            <label data-testid="add-photo-upload"
              className="relative w-24 aspect-[3/4] rounded-xl border-2 border-dashed border-white/15 grid place-items-center text-slate-500 active:scale-95 transition-transform cursor-pointer shrink-0">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-rose-500" /> : <div className="flex flex-col items-center gap-1 pointer-events-none"><Camera className="w-6 h-6" /><span className="text-[10px]">Upload</span></div>}
              <input id="photo-file-input" ref={fileRef} type="file" accept="image/*" data-testid="photo-file-input"
                className="sr-only"
                onChange={onUpload} />
            </label>
          )}
        </div>
        <div className="mb-3 flex gap-2">
          <button data-testid="add-photo-url" onClick={() => setShowUrl((s) => !s)} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-xs font-semibold flex items-center justify-center gap-1.5"><Link2 className="w-4 h-4" />Add photo by URL</button>
          <label data-testid="add-photo-gallery"
            className="relative flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer active:scale-[0.98] transition-transform">
            <ImagePlus className="w-4 h-4" />Add photo from Gallery
            <input type="file" accept="image/*" data-testid="gallery-file-input" onChange={onUpload}
              className="sr-only" />
          </label>
        </div>
        {photos.length < 6 && (
          <div className="mb-6">
            <button data-testid="ai-avatar-button" onClick={() => setShowAvatar(true)}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-rose-500/15 to-rose-600/15 border border-rose-400/30 text-rose-200 text-xs font-bold flex items-center justify-center gap-1.5">
              <Sparkles className="w-4 h-4" />Generate an AI avatar
            </button>
          </div>
        )}
        {showUrl && (
          <div className="flex gap-2 mb-6 -mt-3">
            <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://image-url.jpg"
              className="flex-1 h-10 px-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-rose-500/60" />
            <button onClick={addUrl} className="px-4 rounded-xl bg-rose-500 text-white text-sm font-semibold">Add</button>
          </div>
        )}

        {identityLocked && (
          <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10 flex items-start gap-2.5" data-testid="identity-locked-note">
            <Lock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-400 leading-snug">Your name, date of birth and gender are locked after verification and can't be changed. You can still edit your photos, bio, interests and area.</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex gap-3">
            <L label="First name" className="flex-1">
              <input data-testid="profile-firstname-input" disabled={identityLocked} value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className={`${inputCls} ${identityLocked ? "opacity-60 cursor-not-allowed" : ""}`} placeholder="First name" />
            </L>
            <L label="Last name" className="flex-1">
              <input data-testid="profile-lastname-input" disabled={identityLocked} value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className={`${inputCls} ${identityLocked ? "opacity-60 cursor-not-allowed" : ""}`} placeholder="Last name" />
            </L>
          </div>
          <div className="flex gap-3">
            <L label="Date of birth" className="flex-1">
              <input data-testid="profile-dob-input" type="date" disabled={identityLocked} value={form.dob}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                className={`${inputCls} ${identityLocked ? "opacity-60 cursor-not-allowed" : ""}`} />
            </L>
            <L label="Neighborhood" className="flex-1">
              <button data-testid="profile-neighborhood-input" type="button" onClick={() => setAreaOpen((o) => !o)}
                className={`${inputCls} flex items-center justify-between text-left`}>
                <span className={form.neighborhood ? "text-white" : "text-slate-500"}>{form.neighborhood || "Select area"}</span>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
              {areaOpen && (
                <div className="mt-1.5 max-h-44 overflow-y-auto no-scrollbar rounded-xl bg-[#1D1829] border border-white/10 p-1">
                  {AREAS.map((a) => (
                    <button key={a} type="button" data-testid={`area-option-${a.replace(/\s+/g, "-")}`}
                      onClick={() => { setForm({ ...form, neighborhood: a }); setAreaOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm ${form.neighborhood === a ? "bg-rose-500/20 text-rose-200" : "text-slate-300 hover:bg-white/5"}`}>
                      {a}
                    </button>
                  ))}
                </div>
              )}
            </L>
          </div>

          <L label="Gender">
            <div className="flex gap-2" data-testid="profile-gender-select">
              {GENDERS.map((g) => (
                <button key={g.v} disabled={identityLocked} onClick={() => !identityLocked && setForm({ ...form, gender: g.v })}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${form.gender === g.v ? "bg-gradient-to-r from-rose-500 to-rose-600 text-white" : "bg-white/5 border border-white/10 text-slate-300"} ${identityLocked ? "opacity-60 cursor-not-allowed" : ""}`}>
                  {g.l}
                </button>
              ))}
            </div>
          </L>

          <L label="Bio">
            <div className="flex items-center justify-between mb-1.5 -mt-1">
              <span className="text-[11px] text-slate-500">Let Gemini draft it for you</span>
              <button type="button" data-testid="ai-bio-button" onClick={genBio} disabled={bioLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-rose-300 px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-400/30 active:scale-95 transition-transform disabled:opacity-60">
                {bioLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {form.bio.trim() ? "Improve with AI" : "Write with AI"}
              </button>
            </div>
            <textarea data-testid="profile-bio-input" value={form.bio} maxLength={300} rows={3} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className={`${inputCls} resize-none py-3`} placeholder="Tell people about yourself…" />
            <span className="text-[11px] text-slate-500">{form.bio.length}/300</span>
          </L>

          <L label="Interests">
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((t) => (
                <button key={t} onClick={() => toggleInterest(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${form.interests.includes(t) ? "bg-rose-500/20 border border-rose-400/40 text-rose-200" : "bg-white/5 border border-white/10 text-slate-400"}`}>
                  {form.interests.includes(t) && <Check className="w-3 h-3 inline mr-1" />}{t}
                </button>
              ))}
            </div>
          </L>
        </div>

        <button data-testid="profile-save-button" onClick={save} disabled={saving}
          className="w-full h-12 mt-7 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold active:scale-[0.98] transition-transform disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Save Profile"}
        </button>

        {/* Notifications */}
        <div className="mt-8">
          <label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Notifications</label>
          <div className="mt-2 p-4 rounded-2xl bg-white/5 border border-white/10">
            {pushState === "unsupported" ? (
              <p className="text-sm text-slate-400">Push notifications aren't supported on this browser.</p>
            ) : isIOS() && !isStandalone() ? (
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300 leading-snug">
                  On iPhone, enable push by opening this in Safari, tapping <span className="font-semibold text-white">Share → Add to Home Screen</span>, then open the app from your Home Screen.
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {pushState === "on" ? <Bell className="w-5 h-5 text-emerald-400 shrink-0" /> : <BellOff className="w-5 h-5 text-slate-500 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">Message alerts</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {pushState === "on" ? "On — even when the app is closed" : pushState === "denied" ? "Blocked in browser settings" : "Get notified of new messages"}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="push-toggle-button"
                  onClick={togglePush}
                  disabled={pushState === "working" || pushState === "denied"}
                  className={`shrink-0 px-4 h-9 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 ${pushState === "on" ? "bg-white/10 text-slate-300 border border-white/10" : "bg-gradient-to-r from-rose-500 to-rose-600 text-white"}`}
                >
                  {pushState === "working" ? <Loader2 className="w-4 h-4 animate-spin" /> : pushState === "on" ? "Turn off" : pushState === "denied" ? "Blocked" : "Enable"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav />

      {editSrc && (
        <PhotoEditor imageSrc={editSrc} onCancel={() => setEditSrc(null)} onConfirm={uploadBlob} />
      )}


      {showAvatar && (
        <div className="absolute inset-0 z-[60] flex flex-col justify-end" data-testid="ai-avatar-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !avatarLoading && setShowAvatar(false)} />
          <div className="relative bg-[#15121E] border-t border-white/10 rounded-t-3xl p-6 pb-8 animate-float-up">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-rose-400" />
              <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Generate an AI avatar</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">Describe how you'd like to look. Gemini (Nano Banana) will create a profile picture and add it to your photos.</p>
            <textarea data-testid="ai-avatar-prompt" value={avatarPrompt} onChange={(e) => setAvatarPrompt(e.target.value)} rows={3} maxLength={400}
              placeholder="e.g. a friendly 25-year-old with short hair, wearing a denim jacket, warm smile, outdoors at golden hour"
              className={`${inputCls} resize-none py-3 h-auto mb-4`} />
            <button data-testid="ai-avatar-generate" onClick={genAvatar} disabled={avatarLoading}
              className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF2E63] to-[#FF6B8B] text-white font-bold active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
              {avatarLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating…</> : "Generate avatar"}
            </button>
            <button onClick={() => !avatarLoading && setShowAvatar(false)} className="w-full mt-3 py-2 text-slate-400 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="absolute inset-0 z-[60] bg-[#0D0C12] flex flex-col" data-testid="profile-preview">
          <div className="h-14 px-4 flex items-center justify-between border-b border-white/10 shrink-0">
            <span className="font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>How others see you</span>
            <button data-testid="preview-close" onClick={() => setShowPreview(false)} className="w-9 h-9 grid place-items-center text-slate-300 rounded-full active:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 relative p-4">
            <div className="relative w-full h-full">
              <ProfileCard profile={{
                name: `${form.first_name} ${form.last_name}`.trim() || user.name,
                age: user.age || null, gender: form.gender,
                bio: form.bio, photos: order, interests: form.interests, neighborhood: form.neighborhood,
              }} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const inputCls = "w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm focus:outline-none focus:border-rose-500/60 transition-colors";

function L({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
